import { useI18n, type MessageKey } from '../i18n';
import { useAppStore } from '../state/store';
import { useAudioGraph } from '../hooks/useAudioGraph';
import StartStopButton from './StartStopButton';
import VolumeSlider from './VolumeSlider';

/** Map internal error codes from useAudioGraph to user-facing i18n keys. */
function errorKeysForCode(code: string): { primary: MessageKey; secondary?: MessageKey } {
  switch (code) {
    case 'Permission_denied':
      return { primary: 'error.noMic', secondary: 'error.noMic.help' };
    case 'AudioPlayback_blocked':
      return { primary: 'error.audioBlocked' };
    case 'AudioContext_unsupported':
    case 'AudioElement_missing':
      return { primary: 'error.notSupported' };
    default:
      return { primary: 'error.notSupported' };
  }
}

export default function MainScreen() {
  const { t, locale, setLocale } = useI18n();
  const audioState = useAppStore((s) => s.audioState);
  const errorMessage = useAppStore((s) => s.errorMessage);
  const outputDeviceLabel = useAppStore((s) => s.outputDeviceLabel);
  const { start, stop, registerAudioElement } = useAudioGraph();

  const statusKey: MessageKey =
    audioState === 'running'
      ? 'status.listening'
      : audioState === 'paused'
      ? 'status.paused'
      : audioState === 'starting'
      ? 'status.starting'
      : audioState === 'error'
      ? 'status.error'
      : 'status.idle';

  const errorKeys = errorMessage ? errorKeysForCode(errorMessage) : null;

  return (
    <main className="flex min-h-screen flex-col safe-top safe-bottom px-6">
      <header className="flex items-center justify-between py-4">
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-3"
        >
          <span
            aria-hidden="true"
            className={[
              'inline-block h-3 w-3 rounded-full',
              audioState === 'running' ? 'bg-green-500 animate-pulse' : 'bg-slate-400',
            ].join(' ')}
          />
          <span className="text-xl font-medium">{t(statusKey)}</span>
        </div>
        <button
          type="button"
          onClick={() => setLocale(locale === 'el' ? 'en' : 'el')}
          className="min-h-12 min-w-12 rounded-2xl border border-[var(--border)] px-5 py-3 text-lg"
          aria-label={t('language.toggle.a11y')}
        >
          {locale === 'el' ? 'EN' : 'ΕΛ'}
        </button>
      </header>

      <section className="flex flex-1 flex-col items-center justify-center gap-10">
        <StartStopButton onStart={start} onStop={stop} />

        <VolumeSlider />

        <div
          className="min-h-[4rem] text-center text-[var(--muted)]"
          aria-live="polite"
        >
          {outputDeviceLabel && (
            <p className="truncate max-w-xs" title={outputDeviceLabel}>
              {outputDeviceLabel}
            </p>
          )}
          {errorKeys && (
            <div role="alert" className="mt-1 space-y-1">
              <p className="text-danger-dark dark:text-danger font-medium">
                {t(errorKeys.primary)}
              </p>
              {errorKeys.secondary && (
                <p className="text-base text-[var(--muted)]">
                  {t(errorKeys.secondary)}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <footer className="py-4 text-center text-base text-[var(--muted)]">
        {t('disclaimer.short')}
      </footer>

      <audio
        ref={registerAudioElement}
        playsInline
        autoPlay
        // No controls: PWA owns the UI.
        className="hidden"
      />
    </main>
  );
}
